import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { modals } from "@mantine/modals";

import MapComponent from './MapComponent';
import { FooterMenuViewType } from './components/FooterMenu';
import { openPractitionerListDrawer } from "components/PractitionerListDrawer";
import { openOppsAndRisksDrawer } from "components/OppsAndRisksDrawer";
import { openRoutesDrawer } from "components/RoutesDrawer";
import { openLeadDrawer } from "components/LeadDrawer";
import FunnelModal from './components/FunnelModal';
import { store } from 'stores/Store';
import { useLazyLocalStorage } from '../../hooks/useLazyLocalStorage';
import { DEFAULT_VALUES, FilterControl, withFilterDefaults } from './components/FunnelMenu/FiltersPanel';
import { ColorType } from './components/FunnelMenu/ColorsPanel';
import SearchModal from './components/SearchModal';
import { ViewType } from './components/Header';
import LocationBaseballCardModal from './components/LocationBaseballCardModal';
import { Box } from '@mantine/core';
import PractitionersList from 'Modules/PractitionersList';

const MobileLayout: FC = () => {
  const navigate = useNavigate();


  const [isFunnelModalOpen, setIsFunnelModalOpen] = useState(false);
  const [footerMenuView, setFooterMenuView] = useState<FooterMenuViewType>('MAP');
  const [mapView, setMapView] = useState<ViewType>('map');
  const [listViewPins, setListViewPins] = useState<any[]>([]);
  const [selectedPractitioner, setSelectedPractitioner] = useState<any>(null);
  const [isMapSearchOnView, setIsMapSearchOnView] = useState(false);
  const [hasMapSearchResults, setHasMapSearchResults] = useState(false);

  const [storedFilterControl, setFilterControl] = useLazyLocalStorage<FilterControl>
    ("map-filters", DEFAULT_VALUES);
  // EIS-1088: see DesktopLayout -- normalize stale persisted blobs to "all on".
  const filterControl = useMemo(
    () => withFilterDefaults(storedFilterControl),
    [storedFilterControl]
  );
  const [colorControl, setColorControl] = useLazyLocalStorage<ColorType>
    ("map-colors");

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedLocation, setSelectedLocation] = useState<any | undefined>(undefined);
  const [mapCenterLocation, setMapCenterLocation] = useState<[number, number] | undefined>();
  const [isGoRouteModalOpen, setIsGoRouteModalOpen] = useState(false);

  const setIsSlideUpMenusOpen = (isOpen: boolean) => {
    store.setIsSlideUpMenusOpenInMap(isOpen);
  };

  useEffect(() => {
    setIsSlideUpMenusOpen(false);
    modals.closeAll();
    setIsMapSearchOnView(false);

    if (footerMenuView === 'HOME') {
      navigate('/chatbot');
      return;
    }

    if (footerMenuView === "OPPSANDRISKS") {
      openOppsAndRisksDrawer({ isFullscreen: true });
      return;
    }

    if (footerMenuView === 'PRACTITIONER_LIST') {
      openPractitionerListDrawer({
        isFullscreen: true,
        onFindOnMap: (p, latLon) => {
          // from practitioner list find on map
          modals.closeAll();
          setMapCenterLocation(latLon);
          setSelectedPractitioner(p);
          setFooterMenuView("MAP");
        }
      });
      return;
    }

    if (footerMenuView === "ROUTES") {
      openRoutesDrawer({ isFullscreen: true });
      return;
    }
  }, [footerMenuView, navigate]);

  return (
    <>
      <MapComponent
        mapView={mapView}
        onMapViewChange={(v) => setMapView(v)}
        footerMenuView={footerMenuView}
        colorControl={colorControl}
        filterControl={filterControl}
        searchTerm={searchTerm}
        listViewPins={listViewPins}
        mapCenterLocation={mapCenterLocation}
        isGoRouteModalOpen={isGoRouteModalOpen}
        setIsGoRouteModalOpen={setIsGoRouteModalOpen}
        onFooterMenuViewChange={setFooterMenuView}
        onClickFunnelButton={() => setIsFunnelModalOpen(true)}
        onClickSearchButton={() => setIsSearchModalOpen(true)}
        onSelectPractitioner={() => {
          openPractitionerListDrawer({
            isFullscreen: false,
            noBackNav: true,
            onFindOnMap: (practitionerId, latLon) => {
              // in this case practitioner is alreay selected, we just need to close the modal
              modals.closeAll();
            }
          });
        }}
        onSelectLead={setSelectedLocation}
        toggleViewType={() => {
          setMapView(prev => prev === 'map' ? 'list' : 'map')
        }}
        onListViewPinsChange={setListViewPins}
        //pin states
        selectedPractitioner={selectedPractitioner}
        onSelectedPractitioner={(p) => setSelectedPractitioner(p)}
        onMapSearchChange={(mapSearchState) => {
          setIsMapSearchOnView(mapSearchState);
        }}
        isMapSearchActive={isMapSearchOnView}
      />

      <FunnelModal
        isOpen={isFunnelModalOpen}
        initFilterControl={filterControl}
        initColor={colorControl}
        onApplyFilter={setFilterControl}
        onApplyColor={setColorControl}
        onClose={() => setIsFunnelModalOpen(false)}
      />

      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => {
          setIsSearchModalOpen(false);
        }}
        onSearch={(s) => {
          setSearchTerm(s);
          setIsSearchModalOpen(false);
        }}
      />

      <LocationBaseballCardModal
        onClose={() => {
          setSelectedLocation(undefined);
        }}
        onCreateLead={(li) => {
          openLeadDrawer(li, {}, () => { });
        }}
        onCenterOnMap={setMapCenterLocation}
        onFindOnMap={() => setIsGoRouteModalOpen(true)}
        onPhoneClicked={(phone) => {
          window.open(`tel:${phone}`, '_blank');
        }}
        placeId={selectedLocation}
      />

      {isMapSearchOnView && (
        <Box
          style={{
            zIndex: 6000,
            position: "absolute",
            background: "rgba(255, 255, 255, 0.9)",
            top: 30,
            width: "100%",
            height: !hasMapSearchResults ? 50 : "auto",
            padding: 0,
            margin: 0,
            boxSizing: "border-box",
          }}>
          <PractitionersList
            isDropdown={true}
            hideList={!hasMapSearchResults}
            onSeachTermChange={(searchTerm) => {
              if (searchTerm && searchTerm.length > 0) {
                setHasMapSearchResults(true);
              } else {
                setHasMapSearchResults(false);
              }
            }}
            onPractitionerSelect={(initialTab) => {
              const currentPractitioner = store?.currentPractitioner;
              const defaultAddressId = currentPractitioner?.default_address;
              const address = currentPractitioner?.addresses?.find(
                (x: any) => x.id === defaultAddressId
              );
              if (address) {
                const latLon: [number, number] = [
                  address.latitude,
                  address.longitude
                ];
                setMapCenterLocation(latLon);
                setSelectedPractitioner(currentPractitioner);
              }
              setIsMapSearchOnView(false);
            }}
            onOpenLeadCreate={() => {
            }}
          />
        </Box>
      )}
    </>
  );
};

export default MobileLayout;
